import React, { FC, useEffect, useState } from 'react';
import { Formik } from 'formik';
import { Checkbox, Form, FormItem, Input } from 'formik-antd';
import { defaultFormLayout } from 'common/config/formLayout';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { FormattedMessage } from 'react-intl';
import { SubpartnersRegisterSelect } from 'common/components/SubpartnersSelect';
import { useQuery } from 'react-query';
import { BusinessPartnersAPI } from 'api/BusinessPartnersAPI';
import { QueryKey } from 'api/queryKey';
import { DoctorFormModel, DoctorDTO } from 'models/doctor';
import { SaveButton } from 'common/components/SaveButton';
import { FormikOnSubmit } from 'lib/formik.utils';
import {
  requiredEmailValidationSchema,
  firstNameValidationSchema,
  lastNameValidationSchema,
  phoneNumberValidationSchema,
  medicalSpecializationValidationSchema,
  phoneNumberPrefixCodeValidation,
} from 'common/config/validationSchema';
import { object } from 'yup';
import { useRole } from 'common/hooks/useRole';
import { isNil } from 'ramda';
import { BusinessPartnerContext } from 'pages/BusinessPartnerManagement/BusinessPartnerContext';
import { useEnsureContext } from 'common/hooks';

export interface BusinessPartnerDoctorFormProps {
  value: DoctorFormModel | DoctorDTO;
  loading: boolean;
  onSubmit: FormikOnSubmit<DoctorFormModel>;
  checkedDenyAccess: boolean;
  setCheckedDenyAccess: React.Dispatch<React.SetStateAction<boolean>>;
  setShouldPutPermissions: React.Dispatch<React.SetStateAction<boolean>>;
}

export const BusinessPartnerDoctorForm: FC<BusinessPartnerDoctorFormProps> = ({
  value,
  loading = false,
  onSubmit,
  checkedDenyAccess,
  setCheckedDenyAccess,
  setShouldPutPermissions,
}) => {
  const intlMessage = useFormatMessage();
  const doctorId = value.userId && value.userId;
  const [checked, setChecked] = useState(false);
  const [permissionsSet, setpermissionsSet] = useState(false);
  const { isMedicalOperator } = useRole();

  const validationSchema = object().shape(
    {
      email: requiredEmailValidationSchema(intlMessage),
      firstName: firstNameValidationSchema(intlMessage),
      lastName: lastNameValidationSchema(intlMessage),
      medicalSpecialization: medicalSpecializationValidationSchema(intlMessage),
      phoneNumber: phoneNumberValidationSchema(intlMessage),
      prefixCode: phoneNumberPrefixCodeValidation(intlMessage),
    },
    [['prefixCode', 'phoneNumber']]
  );

  const { data, isSuccess } = useQuery(
    QueryKey.BusinessPartnerDoctor,
    () => BusinessPartnersAPI.fetchUserPermissions(doctorId),
    {
      enabled: !isMedicalOperator,
    }
  );

  useEffect(() => {
    const diagnosisPermission =
      isSuccess &&
      data?.items?.find(
        (item: { permission: string; enabled: boolean }) =>
          item.permission === 'DIAGNOSIS'
      );
    const diagnosisEnabled = diagnosisPermission.scope !== 'OWN';
    setChecked(diagnosisEnabled);
    setCheckedDenyAccess(diagnosisEnabled);
  }, [isSuccess, data, setCheckedDenyAccess]);
  const bpContext = useEnsureContext(BusinessPartnerContext);

  const shouldHidePartnerDropdown =
    !isNil(bpContext.parentPartnerId) || isMedicalOperator;

  //seTimeout used as a temporary solution waiting for the dedicated permissions screen in the app (set to 500 as we have to wait for setCheked() to set to the proper permission from backend)
  setTimeout(() => {
    setpermissionsSet(true);
  }, 500);

  const doctorEditInitialValues = {
    email: value.email,
    firstName: value.firstName,
    lastName: value.lastName,
    medicalSpecialization: value.medicalSpecialization,
    phoneNumber: value.phoneNumber,
    prefixCode: value.prefixCode,
    userId: value.userId,
    denyAccess: !checked,
  };
  return (
    <Formik
      enableReinitialize={true}
      initialValues={doctorEditInitialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      <Form {...defaultFormLayout}>
        <FormItem
          label={intlMessage({ id: 'person.firstName' })}
          name="firstName"
          required
        >
          <Input name="firstName" disabled={isMedicalOperator} />
        </FormItem>

        <FormItem
          label={intlMessage({ id: 'person.lastName' })}
          name="lastName"
          required
        >
          <Input name="lastName" disabled={isMedicalOperator} />
        </FormItem>

        <FormItem
          label={intlMessage({ id: 'person.medicalSpecialization' })}
          name="medicalSpecialization"
        >
          <Input name="medicalSpecialization" disabled={isMedicalOperator} />
        </FormItem>

        <FormItem label={intlMessage({ id: 'email' })} name="email">
          <Input name="email" readOnly disabled />
        </FormItem>

        <FormItem
          name="phone"
          label={intlMessage({ id: 'person.phoneNumber' })}
          style={{ display: 'flex', flexDirection: 'row', width: '100%' }}
        >
          <FormItem
            name="phoneNumber"
            style={{ marginBottom: '0', display: 'flex', width: '100%' }}
          >
            <FormItem
              name="prefixCode"
              style={{ marginBottom: '0', width: 95, display: 'inline-block' }}
            >
              <Input
                name="prefixCode"
                style={{ width: 95 }}
                addonBefore={'+'}
                disabled={isMedicalOperator}
              />
            </FormItem>
            <Input
              style={{ width: 244, marginLeft: 10 }}
              name="phoneNumber"
              disabled={isMedicalOperator}
            />
          </FormItem>
        </FormItem>

        {!shouldHidePartnerDropdown && (
          <FormItem label={intlMessage({ id: 'partner' })} name="partner">
            <SubpartnersRegisterSelect
              defaultValue={
                value.partnerDetails ? value.partnerDetails.name : ''
              }
              name="partner-edit"
              disabled
            />
          </FormItem>
        )}
        {!isMedicalOperator && (
          <FormItem
            name={'denyAccess'}
            style={{ marginLeft: '200px', width: '100%' }}
          >
            {permissionsSet === true && (
              <>
                <Checkbox
                  key="denyAccess"
                  name={'denyAccess'}
                  defaultChecked={!checked}
                  onChange={() => {
                    setCheckedDenyAccess(!checkedDenyAccess);
                    setShouldPutPermissions(true);
                  }}
                />
              </>
            )}{' '}
            <span style={{ marginLeft: '7px' }}>
              {' '}
              <FormattedMessage id="doctors.denyAccessToDiagnosis" />
            </span>
          </FormItem>
        )}
        {!isMedicalOperator && <SaveButton loading={loading} />}
      </Form>
    </Formik>
  );
};
